import axios from "axios";
import GlobalVariables from "./globalVariables";

/**
 * AnnouncementModel
 *
 * Fetches product announcements from the backend for a given display surface.
 * The endpoint is public (no auth required) and returns the most recent
 * announcement currently within its scheduled visibility window, or null.
 *
 * Announcements can be targeted at specific organizations, or at the clients of
 * an integration. The backend resolves the viewer from `uid`/`email` and judges
 * targeting against the organization they are working in, so all three are
 * forwarded when known; without them only all-users announcements come back.
 */
class AnnouncementModel {
  static getLatestEndpoint() {
    return (
      GlobalVariables.getAPIURL() + "/api/announcements/announcements-latest"
    );
  }

  /**
   * Get the latest active announcement for a display surface.
   *
   * @param {("banner"|"modal"|"chat")} surface Display surface to filter by.
   * @param {Object} [viewer] Logged-in viewer, used for audience targeting.
   * @param {string} [viewer.uid] Viewer's user ID.
   * @param {string} [viewer.email] Viewer's account email.
   * @param {string} [viewer.organizationId] Organization currently being viewed.
   * @returns {Promise<Object|null>} Announcement or null when none is active.
   */
  static async getLatestForSurface(surface, viewer = {}) {
    try {
      const params = new URLSearchParams({ displaySurface: surface });
      if (viewer.uid) params.set("uid", viewer.uid);
      if (viewer.email) params.set("email", viewer.email);
      if (viewer.organizationId) {
        params.set("organizationId", viewer.organizationId);
      }
      const url = `${AnnouncementModel.getLatestEndpoint()}?${params.toString()}`;
      const response = await axios.get(url, { timeout: 15000 });
      if (response?.data?.success) {
        return response.data.data || null;
      }
      return null;
    } catch (error) {
      // Announcements are non-critical chrome; never block the chat on failure.
      console.warn(
        "[AnnouncementModel] Failed to load announcement:",
        error?.message || error
      );
      return null;
    }
  }
}

export default AnnouncementModel;
